import React, { useState, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'

import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import Button from 'core/elements/button/Button'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Theme from 'core/themes/model'

interface HostAggregate {
  hostAggregateName: string
  metadata: Record<string, any>
}

interface ExpandableHostAggregateMetadataViewProps {
  itemName: string
  value: HostAggregate[]
}

const ExpandableHostAggregateMetadataView: React.FC<ExpandableHostAggregateMetadataViewProps> = ({
  itemName,
  value,
}) => {
  const classes = useStyles({})
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

  const ResourceLabelsCell = useMemo(
    () => createResourceLabelsCell({ type: 'table', separator: '=' }),
    [],
  )

  const toggleRow = useCallback((rowKey: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }))
  }, [])

  const handleToggle = useCallback(
    (rowKey: string) => (e: React.MouseEvent) => {
      e.preventDefault()
      toggleRow(rowKey)
    },
    [toggleRow],
  )

  return (
    <>
      {value?.map((hostAggregate) => {
        const rowKey = `${itemName}::${hostAggregate.hostAggregateName}`
        const isExpanded = expandedRows[rowKey]

        return (
          <div key={rowKey}>
            <Button
              variant="secondary"
              className={classes.showMoreButton}
              onClick={handleToggle(rowKey)}
            >
              <Text variant="body2">{hostAggregate?.hostAggregateName}</Text>
              <FontAwesomeIcon size="md">
                {isExpanded ? 'chevron-up' : 'chevron-down'}
              </FontAwesomeIcon>
            </Button>

            {isExpanded && (
              <div className={classes.labelsContainer}>
                <ResourceLabelsCell value={hostAggregate?.metadata} />
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

export default ExpandableHostAggregateMetadataView

const useStyles = makeStyles<Theme, { state?: string; responding?: boolean }>((theme) => ({
  showMoreButton: {
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    padding: theme.spacing(0, 1),
    minHeight: 'initial',
    height: 26,
  },
  labelsContainer: {
    marginTop: theme.spacing(1),
  },
}))
